'use client';

import { useMemo } from 'react';

import { REPOSITORY_DATA_SOURCE } from '@/data/installations/constants';
import { useInstallationRepositories } from '@/data/installations/use-installation-repositories';

type UsePrV2RepositoryIdProps = {
  owner: string | null | undefined;
  repo: string | null | undefined;
  installationId: number | null;
  enabled?: boolean;
};

type PrV2RepositoryIdResolution = {
  repositoryId: number | null;
  repositoryIsPrivate: boolean | null;
  // True only while the underlying repositories query is actively fetching, so callers
  // can distinguish a transiently-null id (still resolving) from a settled null.
  isResolving: boolean;
};

export function usePrV2RepositoryIdResolution({
  owner,
  repo,
  installationId,
  enabled = true,
}: UsePrV2RepositoryIdProps): PrV2RepositoryIdResolution {
  const shouldFetch = enabled && Boolean(installationId);
  const { data: installationData, isLoading } = useInstallationRepositories(
    shouldFetch ? installationId : null,
    REPOSITORY_DATA_SOURCE.DB,
  );

  const repository = useMemo(() => {
    const normalizedOwner = owner?.toLowerCase();
    const normalizedRepo = repo?.toLowerCase();

    if (!normalizedOwner || !normalizedRepo) {
      return null;
    }

    return (
      installationData?.repositories.find(
        (repository) =>
          repository.owner.login.toLowerCase() === normalizedOwner &&
          repository.name.toLowerCase() === normalizedRepo,
      ) ?? null
    );
  }, [installationData?.repositories, owner, repo]);

  return {
    repositoryId: repository?.id ?? null,
    repositoryIsPrivate: typeof repository?.private === 'boolean' ? repository.private : null,
    isResolving: shouldFetch && isLoading,
  };
}

export function usePrV2RepositoryId(props: UsePrV2RepositoryIdProps): number | null {
  return usePrV2RepositoryIdResolution(props).repositoryId;
}
